import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  DEFAULT_PLOT_GROUP_FILTERS,
  DEFAULT_PLOT_GROUP_GROUP_BY,
  DEFAULT_PLOT_GROUP_SORT,
  PlotGroup,
  PlotGroupFiltersEnum,
  PlotGroupGroupByEnum,
  PlotLabelName,
  PlotLabelValue,
} from '@/api/rest/resources/types/plotGroup';
import { parsePlotGroupBodyData } from '@/components/PlotGroups/utils/parsePlotGroupBodyData';
import { useAuth } from '@/lib/auth';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationPlotGroups } from '@/pages/shared/hooks/useOrganizationPlotGroup';

/**
 * Group names logic:
 * - default: the main plot group, used when the filters / sorting are applied
 * - no grouping: is a clone of the default group without the grouping information. This allows us to get the plots belonging to the group (once this is available from the API, we should delete this)
 * - details: used for the details view. This group allows us the keep track of the user selection (group selected by the user), while keeping the "default" group intact
 */
export const usePlotGroupNames = () => {
  const { authenticatedUser } = useAuth();

  const userId = authenticatedUser.user?.id ?? 'unknown';

  return {
    DEFAULT: `default_${userId}`,
    NO_GROUPING: `default_no_grouping_${userId}`,
    DETAILS: `details_${userId}`,
  };
};

export const usePlotGroupDefault = (group: 'default' | 'details' | 'no_grouping'): PlotGroup => {
  const { DEFAULT, DETAILS, NO_GROUPING } = usePlotGroupNames();

  const defaultGroup = {
    id: null,
    groupBy: DEFAULT_PLOT_GROUP_GROUP_BY,
    filters: DEFAULT_PLOT_GROUP_FILTERS,
    sort: DEFAULT_PLOT_GROUP_SORT,
    name: DEFAULT,
    active: true,
  };

  switch (group) {
    case 'default':
      return {
        ...defaultGroup,
        name: DEFAULT,
        active: true,
      };
    case 'details':
      return {
        ...defaultGroup,
        name: DETAILS,
        active: false,
      };
    case 'no_grouping':
      return {
        ...defaultGroup,
        name: NO_GROUPING,
        active: false,
      };
    default:
      throw new Error(`Unknown group: ${group}`);
  }
};

export type PlotGroupContextInterface = {
  plotGroups: PlotGroup[];
  setPlotGroups: Dispatch<SetStateAction<PlotGroup[]>>;
  /** ID of the DETAILS group written to DB for transient (in-page) filter overrides.
   *  When set, API calls use this ID instead of the URL param so the named group
   *  in the DB is never mutated. Cleared on page reload automatically. */
  transientPlotGroupId: string | null;
  setTransientPlotGroupId: (id: string | null) => void;
};

export const PlotGroupContext = createContext<PlotGroupContextInterface | null>(null);

export const PlotGroupProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [plotGroups, setPlotGroupsRaw] = useState<PlotGroup[]>([]);
  const [transientPlotGroupId, setTransientPlotGroupId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const queryParamPlotGroupId = searchParams.get('plotGroupId');

  const setPlotGroups = useCallback<Dispatch<SetStateAction<PlotGroup[]>>>((action) => setPlotGroupsRaw(action), []);

  const value = useMemo<PlotGroupContextInterface>(
    () => ({ plotGroups, setPlotGroups, transientPlotGroupId, setTransientPlotGroupId }),
    [plotGroups, setPlotGroups, transientPlotGroupId, setTransientPlotGroupId],
  );

  if (queryParamPlotGroupId) {
    return (
      <Suspense>
        <PlotGroupFromQueryParamSync value={value}>{children}</PlotGroupFromQueryParamSync>
      </Suspense>
    );
  }

  return <PlotGroupContext.Provider value={value}>{children}</PlotGroupContext.Provider>;
};

const PlotGroupFromQueryParamSync = ({
  value,
  children,
}: {
  value: PlotGroupContextInterface;
  children: ReactNode;
}) => {
  const plotGroupFromQueryParam = useGetPlotGroupFromQueryParam();

  const { setPlotGroups } = value;

  useEffect(() => {
    if (plotGroupFromQueryParam) {
      setPlotGroups((prev) => {
        const isInPlotGroups = prev.some((pg) => pg.id === plotGroupFromQueryParam.id);
        if (isInPlotGroups) {
          return prev.map((pg) =>
            pg.id === plotGroupFromQueryParam.id ? { ...pg, active: true } : { ...pg, active: false },
          );
        }
        return [...prev, plotGroupFromQueryParam];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <PlotGroupContext.Provider value={value}>{children}</PlotGroupContext.Provider>;
};

export const usePlotGroupContext = (): PlotGroupContextInterface => {
  const plotGroupContextData = useContext(PlotGroupContext);

  if (!plotGroupContextData) {
    throw new Error(
      `The component that calls usePlotGroupContext() needs to be a deep child of <PlotGroupContextProvider>`,
    );
  }

  return plotGroupContextData;
};

export const useActivePlotGroup = (): PlotGroup => {
  const { plotGroups } = usePlotGroupContext();
  const defaultPlotGroup = usePlotGroupDefault('default');

  return plotGroups.find((pg) => pg.active) || defaultPlotGroup;
};

export const usePlotGroup = () => {
  return useActivePlotGroup();
};

export const usePlotGroupWithoutGrouping = () => {
  const orgId = useOrganizationId();
  const savedPlotGroups = useOrganizationPlotGroups(orgId, []).data;
  const plotGroups = savedPlotGroups.map((pg) => parsePlotGroupBodyData(pg, false));

  const { plotGroups: plotGroupsFromContext } = usePlotGroupContext();

  const { NO_GROUPING } = usePlotGroupNames();

  if (plotGroupsFromContext.every((pg) => !pg.active)) {
    // no Plot Group is active, then the no grouping plot group should not be applied as a filter
    return null;
  }

  return plotGroups.find((pg) => pg.name === NO_GROUPING) || null;
};

export const useSetPlotGroupId = () => {
  const { setPlotGroups } = usePlotGroupContext();

  const setActivePlotGroupId = (id: string | null) => {
    setPlotGroups((prev) => {
      return prev.map((pg) => {
        if (pg.active) {
          return { ...pg, id };
        }
        return pg;
      });
    });
  };

  return { setActivePlotGroupId };
};

export const useSetPlotGroupName = () => {
  const { setPlotGroups } = usePlotGroupContext();

  const setActivePlotGroupName = (name: string | null) => {
    setPlotGroups((prev) => {
      return prev.map((pg) => {
        if (pg.active) {
          return { ...pg, name };
        }
        return pg;
      });
    });
  };

  return { setActivePlotGroupName };
};

export const usePlotGroupId = () => {
  const [searchParams] = useSearchParams();
  const queryParamPlotGroupId = searchParams.get('plotGroupId');

  const activePlotGroupId = useActivePlotGroup().id;
  const { plotGroups, setPlotGroups, transientPlotGroupId } = usePlotGroupContext();

  useEffect(() => {
    if (!queryParamPlotGroupId) return;

    const isInPlotGroups = plotGroups.some((pg) => pg.id === queryParamPlotGroupId);
    const isActive = queryParamPlotGroupId === activePlotGroupId;

    if (isInPlotGroups && !isActive) {
      setPlotGroups((prev) => prev.map((pg) => ({ ...pg, active: pg.id === queryParamPlotGroupId })));
    }
  }, [queryParamPlotGroupId, plotGroups, activePlotGroupId, setPlotGroups]);

  // When transient filters are active (user filtered on top of a named group),
  // return the DETAILS group id so all API data-fetching hooks use the filtered
  // group without mutating the named group in the DB.
  if (transientPlotGroupId) {
    return transientPlotGroupId;
  }

  if (!queryParamPlotGroupId) {
    return activePlotGroupId;
  }
  return queryParamPlotGroupId;
};

export const useSetPlotGroup = () => {
  const { setPlotGroups } = usePlotGroupContext();

  const { DEFAULT } = usePlotGroupNames();

  const addPlotGroup = (plotGroup: PlotGroup, resetPlotGroups?: boolean) => {
    const activatePlotGroup = { ...plotGroup, active: true };

    if (resetPlotGroups) {
      setPlotGroups([activatePlotGroup]);
      return;
    }

    if (plotGroup.name === DEFAULT) {
      // If the plot group is the default one, we want to reset all other plot groups
      setPlotGroups([activatePlotGroup]);
      return;
    }

    setPlotGroups((prev) => {
      const inactivePlotGroups = prev
        .filter((pg) => {
          const hasSameId = pg.id === plotGroup.id;
          const hasSameName = pg.name === plotGroup.name;

          return !hasSameId && !hasSameName;
        })
        .map((pg) => ({ ...pg, active: false }));

      return [...inactivePlotGroups, activatePlotGroup];
    });
  };

  return { addPlotGroup };
};

export const useActivePlotGroupPlotLabel: () => {
  labelName: PlotLabelName | null;
  labelValue: PlotLabelValue[] | null;
} = () => {
  const activePlotGroup = useActivePlotGroup();

  const labelName = activePlotGroup.groupBy[PlotGroupGroupByEnum.labels] ?? null;
  const labelValue = activePlotGroup.filters?.[PlotGroupFiltersEnum.plot__labels__id] ?? null;

  return {
    labelName,
    labelValue,
  };
};

export const useDetailsPlotGroup = (): PlotGroup => {
  const { plotGroups } = usePlotGroupContext();
  const activePlotGroup = useActivePlotGroup();

  const { DETAILS } = usePlotGroupNames();

  const existingDetailsPlotGroup = plotGroups.find((pg) => pg.name === DETAILS);

  if (existingDetailsPlotGroup) {
    return existingDetailsPlotGroup;
  }

  // If it does not exist, we generate a details plot group from the active plot group
  return {
    ...activePlotGroup,
    id: null,
    name: DETAILS,
    active: false,
  };
};

const useGetPlotGroupFromQueryParam = (): PlotGroup | null => {
  const [searchParams] = useSearchParams();
  const queryParamPlotGroupId = searchParams.get('plotGroupId');
  const orgId = useOrganizationId();
  const savedPlotGroups = useOrganizationPlotGroups(orgId, []).data; // [] here enables fetching the internal PGs when these are not available on page load

  const queryParamPlotGroup = savedPlotGroups.find((pg) => pg.id === queryParamPlotGroupId);

  return queryParamPlotGroup ? parsePlotGroupBodyData(queryParamPlotGroup) : null;
};
